// Document Template Service for Admin Management
// Handles firm-wide document template operations (admin only)

export interface DocumentTemplate {
  template_id: string;
  realm_id: string;
  name: string;
  description?: string;
  template_type: 'contract' | 'pleading' | 'correspondence' | 'form' | 'research' | 'discovery' | 'other';
  status: 'active' | 'inactive' | 'archived';
  practice_areas?: string[];
  file_extensions?: string[];
  template_content?: string;
  content_format?: 'html' | 'markdown' | 'lexical' | 'plaintext';
  template_variables?: Record<string, any>;
  template_metadata?: Record<string, any>;
  is_default: boolean;
  is_enabled: boolean;
  usage_count: number;
  last_used_at?: string;
  created_by_user_id: string;
  updated_by_user_id?: string;
  created_at: string;
  updated_at: string;
  deleted_at?: string;
  created_by?: {
    id: string;
    firstName?: string;
    lastName?: string;
    email: string;
  };
  updated_by?: {
    id: string;
    firstName?: string;
    lastName?: string;
    email: string;
  };
}

export interface DocumentTemplateWithUsage extends DocumentTemplate {
  document_count: number;
  can_edit: boolean;
  can_delete: boolean;
}

export interface CreateDocumentTemplateRequest {
  name: string;
  description?: string;
  template_type: DocumentTemplate['template_type'];
  practice_areas?: string[];
  file_extensions?: string[];
  template_content?: string;
  content_format?: DocumentTemplate['content_format'];
  template_variables?: Record<string, any>;
  template_metadata?: Record<string, any>;
  is_default?: boolean;
  is_enabled?: boolean;
}

export interface UpdateDocumentTemplateRequest {
  name?: string;
  description?: string;
  template_type?: DocumentTemplate['template_type'];
  practice_areas?: string[];
  file_extensions?: string[];
  template_content?: string;
  content_format?: DocumentTemplate['content_format'];
  template_variables?: Record<string, any>;
  template_metadata?: Record<string, any>;
  is_default?: boolean;
  is_enabled?: boolean;
  status?: DocumentTemplate['status'];
}

export interface DocumentTemplateFilterOptions {
  template_type?: DocumentTemplate['template_type'];
  status?: DocumentTemplate['status'];
  practice_area?: string;
  is_enabled?: boolean;
  search?: string;
  include_usage_count?: boolean;
}

export interface DocumentTemplateAnalytics {
  total_templates: number;
  active_templates: number;
  templates_by_type: Record<string, number>;
  most_used_templates: Array<{ name: string; usage_count: number }>;
  recent_templates: Array<{ name: string; created_at: string }>;
}

export interface TemplateType {
  value: string;
  label: string;
}

export interface TemplateStatus {
  value: string;
  label: string;
}

class DocumentTemplateService {
  private baseUrl = '/api/v1/dms/admin/templates'; // Full path like working services

  private getAuthHeaders(): HeadersInit {
    const token = localStorage.getItem('webelieve_access_token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }

  // Get all templates for the firm/realm
  async getTemplates(filters?: DocumentTemplateFilterOptions): Promise<{
    templates: DocumentTemplateWithUsage[];
    total: number;
    filters_applied: DocumentTemplateFilterOptions;
  }> {
    try {
      const queryParams = new URLSearchParams();
      
      if (filters?.template_type) queryParams.append('template_type', filters.template_type);
      if (filters?.status) queryParams.append('status', filters.status);
      if (filters?.practice_area) queryParams.append('practice_area', filters.practice_area);
      if (filters?.is_enabled !== undefined) queryParams.append('is_enabled', filters.is_enabled.toString());
      if (filters?.search) queryParams.append('search', filters.search);
      if (filters?.include_usage_count !== undefined) queryParams.append('include_usage_count', filters.include_usage_count.toString());

      const url = queryParams.toString() ? `${this.baseUrl}?${queryParams}` : this.baseUrl;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      
      // Backend returns: { message, templates: [...], total, filters_applied }
      // Extract the templates array from the response
      const templatesArray = data.templates || [];
      
      // Transform backend response to match frontend expectations
      return {
        templates: templatesArray.map((template: any) => ({
          ...template,
          // Ensure both usage_count and document_count are available for compatibility
          usage_count: template.document_count || template.usage_count || 0,
          document_count: template.document_count || template.usage_count || 0,
          can_edit: (template.document_count || template.usage_count || 0) === 0 || !template.is_enabled,
          can_delete: (template.document_count || template.usage_count || 0) === 0 && !template.is_default
        })),
        total: data.total || templatesArray.length,
        filters_applied: data.filters_applied || filters || {}
      };
    } catch (error) {
      console.error('Error fetching templates:', error);
      throw error;
    }
  }

  // Alias method for compatibility
  async getAllTemplatesWithUsage(): Promise<DocumentTemplateWithUsage[]> {
    const response = await this.getTemplates({ include_usage_count: true });
    return response.templates;
  }

  // Get template analytics
  async getTemplateAnalytics(): Promise<{
    message: string;
    analytics: DocumentTemplateAnalytics;
  }> {
    const response = await fetch(`${this.baseUrl}/analytics`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    return response.json();
  }

  // Update template
  async updateTemplate(templateId: string, updateData: UpdateDocumentTemplateRequest): Promise<{
    message: string;
    template: DocumentTemplate;
  }> {
    const response = await fetch(`${this.baseUrl}/${templateId}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(updateData),
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    return response.json();
  }

  // Delete template
  async deleteTemplate(templateId: string): Promise<{
    message: string;
  }> {
    const response = await fetch(`${this.baseUrl}/${templateId}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    return response.json();
  }
}

export const documentTemplateService = new DocumentTemplateService();
export default documentTemplateService;